import React, { useState, useEffect } from "react";
import UserScreenWindow from "../User/Components/UserScreenWindow";
import { useSelector } from "react-redux";
import {
  queryAllDocsByFieldSnap,
  updateDocById,
  getAllDocsSnap,
} from "../../utils/firebase/Firebase.utils";
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiClipboard,
  FiClock,
  FiSearch,
  FiFilter,
  FiChevronRight,
  FiInfo,
  FiX,
  FiBell,
} from "react-icons/fi";

export default function Alerts() {
  const currentUser = useSelector((state) => state.userObject);
  const [activeEmergencies, setActiveEmergencies] = useState([]);
  const [resolvedEmergencies, setResolvedEmergencies] = useState([]);
  const [selectedEmergency, setSelectedEmergency] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [incidentReports, setIncidentReports] = useState([]);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [activeTab, setActiveTab] = useState("emergencies");
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [actionInput, setActionInput] = useState("");

  useEffect(() => {
    // Subscribe to all emergencies collection
    const emergencyUnsubscribe = queryAllDocsByFieldSnap(
      "emergencies",
      "unitNumber",
      "!=",
      "", // This will fetch all records
      (emergencyData) => {
        // Split emergencies by status
        const active = emergencyData.filter((e) => e.status === "Active");
        const resolved = emergencyData.filter((e) => e.status === "Resolved");

        // Sort by most recent first
        const sortByTimestamp = (a, b) => {
          const dateA = new Date(a.timestamp || a.createdAt || 0);
          const dateB = new Date(b.timestamp || b.createdAt || 0);
          return dateB - dateA;
        };

        setActiveEmergencies(active.sort(sortByTimestamp));
        setResolvedEmergencies(resolved.sort(sortByTimestamp));
      }
    );

    // Subscribe to incident reports
    const incidentUnsubscribe = getAllDocsSnap("incidentReports", (data) => {
      setIncidentReports(data);
      setIsLoading(false);
    });

    // Cleanup subscriptions on unmount
    return () => {
      if (emergencyUnsubscribe) emergencyUnsubscribe();
      if (incidentUnsubscribe) incidentUnsubscribe();
    };
  }, []);

  const addAction = async () => {
    if (!actionInput.trim()) return;

    try {
      const newAction = {
        action: actionInput.trim(),
        addedBy: currentUser.displayName || currentUser.email,
        addedById: currentUser.uid,
        timestamp: new Date().toISOString(),
      };

      const updatedEmergency = {
        ...selectedEmergency,
        actions: [...(selectedEmergency.actions || []), newAction],
      };

      // Update Firebase
      await updateDocById(
        "emergencies",
        selectedEmergency.id,
        updatedEmergency
      );

      // Update local state
      setSelectedEmergency(updatedEmergency);
      setActionInput("");
    } catch (error) {
      console.error("Error adding action:", error);
      alert("Failed to add action");
    }
  };

  const resolveEmergency = async () => {
    try {
      if (!resolutionNotes.trim()) {
        alert("Please provide resolution notes");
        return;
      }

      const resolvedEmergency = {
        ...selectedEmergency,
        status: "Resolved",
        resolvedAt: new Date().toISOString(),
        resolutionNotes: resolutionNotes,
        resolvedBy: currentUser.displayName || currentUser.email,
        resolvedById: currentUser.uid,
      };

      await updateDocById(
        "emergencies",
        selectedEmergency.id,
        resolvedEmergency
      );
      setSelectedEmergency(null);
      setResolutionNotes("");
    } catch (error) {
      console.error("Error resolving emergency:", error);
      alert("Failed to resolve emergency");
    }
  };

  // Helper function to get priority-based styling
  const getPriorityColor = (priority) => {
    switch (priority) {
      case "High":
        return "border-red-500 bg-red-50";
      case "Medium":
        return "border-orange-500 bg-orange-50";
      case "Low":
        return "border-yellow-500 bg-yellow-50";
      default:
        return "border-gray-500 bg-gray-50";
    }
  };

  // Helper function to get emergency type icon
  const getEmergencyIcon = (type) => {
    const emergencyTypes = {
      "Fire Alarm": "🔥",
      "Water Leak": "💧",
      "Medical Emergency": "🚑",
      "Security Breach": "🚨",
      "Elevator Malfunction": "⚠️",
    };
    return emergencyTypes[type] || "⚠️";
  };

  // Component for emergency card
  const EmergencyCard = ({ emergency, onClick }) => (
    <button
      onClick={() => onClick(emergency)}
      className={`w-full mb-3 p-4 rounded-lg border-l-4 shadow-sm hover:shadow-md transition-all ${getPriorityColor(
        emergency.priority
      )}`}
    >
      <div className="flex items-center space-x-3">
        <span className="text-2xl">{getEmergencyIcon(emergency.type)}</span>
        <div className="flex-1 text-left">
          <div className="font-semibold">Unit {emergency.unitNumber}</div>
          <div className="text-sm text-gray-600">{emergency.type}</div>
          <div className="text-xs text-gray-500">
            {new Date(
              emergency.timestamp || emergency.createdAt
            ).toLocaleString()}
          </div>
        </div>
        <div className="flex flex-col items-end">
          <div className="inline-flex px-2 py-1 text-xs font-semibold rounded-full">
            {emergency.priority} Priority
          </div>
          <FiChevronRight className="mt-2 text-gray-400" />
        </div>
      </div>
    </button>
  );

  // Component for incident card
  const IncidentCard = ({ incident, onClick }) => {
    const getStatusColor = (status) => {
      switch (status?.toLowerCase()) {
        case "resolved":
          return "text-green-600 bg-green-50";
        case "pending":
          return "text-orange-500 bg-orange-50";
        default:
          return "text-gray-600 bg-gray-50";
      }
    };

    return (
      <button
        onClick={() => onClick(incident)}
        className="w-full mb-3 p-4 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all bg-white"
      >
        <div className="flex items-center space-x-3">
          <div
            className="p-2 rounded-full"
            style={{ backgroundColor: "#F3F4F6" }}
          >
            <FiAlertTriangle style={{ color: "#363062" }} />
          </div>
          <div className="flex-1 text-left">
            <div className="font-semibold" style={{ color: "#363062" }}>
              {incident.incidentType || "Incident Report"}
            </div>
            <div className="text-sm text-gray-600">
              {incident.location || "No location specified"}
            </div>
            <div className="text-xs text-gray-500">
              {new Date(
                incident.timestamp || incident.date || 0
              ).toLocaleString()}
            </div>
          </div>
          <div className="flex flex-col items-end">
            <div
              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                incident.status
              )}`}
            >
              {incident.status || "New"}
            </div>
            <FiChevronRight className="mt-2 text-gray-400" />
          </div>
        </div>
      </button>
    );
  };

  // Calculate statistics for incidents
  const incidentStats = {
    totalIncidents: incidentReports.length,
    resolvedIncidents: incidentReports.filter(
      (report) => report.status?.toLowerCase() === "resolved"
    ).length,
    pendingIncidents: incidentReports.filter(
      (report) => report.status?.toLowerCase() === "pending"
    ).length,
    recentIncidents: incidentReports.filter((report) => {
      const reportDate = new Date(report.timestamp || report.date || 0);
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      return reportDate >= sevenDaysAgo;
    }).length,
  };

  // Filter reports based on search term
  const filteredIncidents = searchTerm
    ? incidentReports.filter(
        (report) =>
          report.incidentType
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          report.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          report.description
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          report.reportedBy?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : incidentReports;

  return (
    <UserScreenWindow label="Security Alerts" backButton={true}>
      <div className="p-4">
        {isLoading ? (
          <div className="flex justify-center items-center h-40">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <>
            {/* Header Section */}
            <div className="flex items-center justify-between mb-6 bg-[#363062]/5 p-4 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-[#363062] rounded-lg">
                  <FiBell className="text-white text-base" />
                </div>
                <div>
                  <h1 className="text-sm font-medium text-[#363062]">
                    Emergency Alerts
                  </h1>
                  <p className="text-xs text-gray-500">
                    Manage emergency alerts and incidents
                  </p>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="mb-4 overflow-x-auto">
              <div className="flex space-x-2 pb-2">
                <button
                  className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                    activeTab === "emergencies"
                      ? "bg-[#363062] text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                  onClick={() => setActiveTab("emergencies")}
                >
                  Emergencies
                </button>
                <button
                  className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                    activeTab === "incidents"
                      ? "bg-[#8FCD00] text-white"
                      : "bg-[#8FCD00]/10 text-gray-700 hover:bg-[#8FCD00]/20"
                  }`}
                  onClick={() => setActiveTab("incidents")}
                >
                  Incident Reports
                </button>
              </div>
            </div>

            {/* Search bar */}
            <div className="relative mb-4">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FiSearch className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Emergency Detail Modal */}
            {selectedEmergency && (
              <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
                  <div className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h2
                          className="text-lg font-bold"
                          style={{ color: "#363062" }}
                        >
                          Emergency: Unit {selectedEmergency.unitNumber}
                        </h2>
                        <p className="text-sm text-gray-500">
                          {new Date(
                            selectedEmergency.timestamp ||
                              selectedEmergency.createdAt
                          ).toLocaleString()}
                        </p>
                      </div>
                      <button
                        onClick={() => setSelectedEmergency(null)}
                        className="text-gray-400 hover:text-gray-500"
                      >
                        <svg
                          className="h-6 w-6"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>

                    <div className="space-y-4">
                      <div className="flex items-center space-x-2">
                        <span
                          className={`px-2 py-1 text-xs font-semibold text-white rounded-full ${
                            selectedEmergency.priority === "High"
                              ? "bg-red-600"
                              : selectedEmergency.priority === "Medium"
                              ? "bg-orange-500"
                              : "bg-yellow-500"
                          }`}
                        >
                          {selectedEmergency.priority} Priority
                        </span>
                        <span
                          className={`px-2 py-1 text-xs font-semibold text-white rounded-full ${
                            selectedEmergency.status === "Active"
                              ? "bg-[#8FCD00]"
                              : "bg-[#4299E1]"
                          }`}
                        >
                          {selectedEmergency.status}
                        </span>
                      </div>

                      <div>
                        <h3
                          className="font-semibold"
                          style={{ color: "#363062" }}
                        >
                          Type
                        </h3>
                        <p className="text-gray-700">
                          {selectedEmergency.type}
                        </p>
                      </div>

                      <div>
                        <h3
                          className="font-semibold"
                          style={{ color: "#363062" }}
                        >
                          Details
                        </h3>
                        <p className="text-gray-700">
                          {selectedEmergency.details}
                        </p>
                      </div>

                      <div>
                        <h3 className="font-semibold text-[#363062] mb-2">
                          Actions Taken
                        </h3>
                        {selectedEmergency.status === "Active" && (
                          <div className="flex mt-1">
                            <input
                              type="text"
                              value={actionInput}
                              onChange={(e) => setActionInput(e.target.value)}
                              className="block w-full rounded-l-md border-gray-300 shadow-sm focus:border-[#363062] focus:ring-[#363062] sm:text-sm"
                              placeholder="Add an action"
                            />
                            <button
                              type="button"
                              onClick={addAction}
                              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-r-md text-white bg-[#363062] hover:bg-[#363062]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#363062]"
                            >
                              Add
                            </button>
                          </div>
                        )}
                        <div className="mt-2 space-y-2">
                          {selectedEmergency.actions?.map((action, index) => (
                            <div
                              key={index}
                              className="bg-gray-50 p-3 rounded-md"
                            >
                              <div className="flex justify-between items-start">
                                <p className="text-sm text-gray-700">
                                  {action.action}
                                </p>
                              </div>
                              <div className="mt-1 flex items-center text-xs text-gray-500">
                                <span>{action.addedBy}</span>
                                <span className="mx-1">•</span>
                                <span>
                                  {new Date(action.timestamp).toLocaleString()}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Resolution Notes Section (for active emergencies) */}
                      {selectedEmergency.status === "Active" && (
                        <div className="mt-4 p-4 bg-gray-50 rounded-md">
                          <h3 className="font-semibold text-[#363062] mb-2">
                            Resolution Notes
                          </h3>
                          <textarea
                            value={resolutionNotes}
                            onChange={(e) => setResolutionNotes(e.target.value)}
                            placeholder="Describe how the emergency was resolved..."
                            rows={3}
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#363062] focus:ring-[#363062] sm:text-sm"
                          />
                          <button
                            onClick={resolveEmergency}
                            className="mt-2 inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white sm:text-sm bg-[#8FCD00] hover:bg-[#8FCD00]/90"
                          >
                            Mark as Resolved
                          </button>
                        </div>
                      )}

                      {/* Display resolution notes if available */}
                      {selectedEmergency.resolutionNotes && (
                        <div className="mt-4 p-4 bg-gray-50 rounded-md">
                          <h3 className="font-semibold text-[#363062]">
                            Resolution Notes
                          </h3>
                          <p className="text-gray-700">
                            {selectedEmergency.resolutionNotes}
                          </p>
                          <div className="mt-2 text-xs text-gray-500">
                            <p>Resolved by: {selectedEmergency.resolvedBy}</p>
                            <p>
                              Resolved on:{" "}
                              {new Date(
                                selectedEmergency.resolvedAt
                              ).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Incident Detail Modal */}
            {selectedIncident && (
              <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
                  <div className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h2
                          className="text-lg font-bold"
                          style={{ color: "#363062" }}
                        >
                          {selectedIncident.incidentType || "Incident Report"}
                        </h2>
                        <p className="text-sm text-gray-500">
                          {new Date(
                            selectedIncident.timestamp ||
                              selectedIncident.date ||
                              0
                          ).toLocaleString()}
                        </p>
                      </div>
                      <button
                        onClick={() => setSelectedIncident(null)}
                        className="text-gray-400 hover:text-gray-500"
                      >
                        <svg
                          className="h-6 w-6"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>

                    <div className="space-y-4">
                      <div className="flex items-center space-x-2">
                        <span
                          className={`px-2 py-1 text-xs font-semibold text-white rounded-full ${
                            selectedIncident.status?.toLowerCase() ===
                            "resolved"
                              ? "bg-green-600"
                              : selectedIncident.status?.toLowerCase() ===
                                "pending"
                              ? "bg-orange-500"
                              : "bg-gray-600"
                          }`}
                        >
                          {selectedIncident.status || "New"}
                        </span>
                      </div>

                      <div>
                        <h3
                          className="font-semibold"
                          style={{ color: "#363062" }}
                        >
                          Location
                        </h3>
                        <p className="text-gray-700">
                          {selectedIncident.location || "Not specified"}
                        </p>
                      </div>

                      <div>
                        <h3
                          className="font-semibold"
                          style={{ color: "#363062" }}
                        >
                          Description
                        </h3>
                        <p className="text-gray-700">
                          {selectedIncident.description ||
                            "No description provided"}
                        </p>
                      </div>

                      <div>
                        <h3
                          className="font-semibold"
                          style={{ color: "#363062" }}
                        >
                          Reported By
                        </h3>
                        <p className="text-gray-700">
                          {selectedIncident.reportedBy || "Anonymous"}
                        </p>
                      </div>

                      {selectedIncident.images &&
                        selectedIncident.images.length > 0 && (
                          <div>
                            <h3
                              className="font-semibold"
                              style={{ color: "#363062" }}
                            >
                              Images
                            </h3>
                            <div className="flex flex-wrap gap-2 mt-2">
                              {selectedIncident.images.map((image, index) => (
                                <img
                                  key={index}
                                  src={image}
                                  alt={`Incident ${index + 1}`}
                                  className="w-24 h-24 object-cover rounded-md"
                                />
                              ))}
                            </div>
                          </div>
                        )}

                      {/* Notes and followup section */}
                      {selectedIncident.notes && (
                        <div className="mt-4 p-4 bg-gray-50 rounded-md">
                          <h3
                            className="font-semibold"
                            style={{ color: "#363062" }}
                          >
                            Notes
                          </h3>
                          <p className="text-gray-700">
                            {selectedIncident.notes}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "emergencies" && (
              <>
                {/* Status Filter Buttons */}
                <div className="mb-4 overflow-x-auto">
                  <div className="flex space-x-2 pb-2">
                    <button
                      className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                        !activeFilter || activeFilter === "all"
                          ? "bg-[#363062] text-white"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                      onClick={() => setActiveFilter("all")}
                    >
                      All (
                      {activeEmergencies.length + resolvedEmergencies.length})
                    </button>
                    <button
                      className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                        activeFilter === "active"
                          ? "bg-[#8FCD00] text-white"
                          : "bg-[#8FCD00]/10 text-gray-700 hover:bg-[#8FCD00]/20"
                      }`}
                      onClick={() => setActiveFilter("active")}
                    >
                      Active ({activeEmergencies.length})
                    </button>
                    <button
                      className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                        activeFilter === "resolved"
                          ? "bg-[#4299E1] text-white"
                          : "bg-[#4299E1]/10 text-gray-700 hover:bg-[#4299E1]/20"
                      }`}
                      onClick={() => setActiveFilter("resolved")}
                    >
                      Resolved ({resolvedEmergencies.length})
                    </button>
                  </div>
                </div>

                {/* Active Emergencies Section */}
                <div className="mb-6">
                  <h2 className="text-base font-bold mb-3 text-[#363062]">
                    Active Emergencies
                    {activeEmergencies.length > 0 && (
                      <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        {activeEmergencies.length}
                      </span>
                    )}
                  </h2>

                  {activeEmergencies.length > 0 ? (
                    activeEmergencies
                      .filter((emergency) =>
                        searchTerm
                          ? emergency.unitNumber
                              ?.toLowerCase()
                              .includes(searchTerm.toLowerCase()) ||
                            emergency.type
                              ?.toLowerCase()
                              .includes(searchTerm.toLowerCase()) ||
                            emergency.details
                              ?.toLowerCase()
                              .includes(searchTerm.toLowerCase())
                          : true
                      )
                      .map((emergency) => (
                        <button
                          key={emergency.id}
                          onClick={() => setSelectedEmergency(emergency)}
                          className="w-full mb-3 p-4 rounded-lg shadow-sm hover:shadow-md transition-all bg-white border border-gray-100"
                        >
                          <div className="flex items-center space-x-3">
                            <span className="text-2xl">
                              {getEmergencyIcon(emergency.type)}
                            </span>
                            <div className="flex-1 text-left">
                              <div className="font-semibold text-[#363062]">
                                Unit {emergency.unitNumber}
                              </div>
                              <div className="text-sm text-gray-600">
                                {emergency.type}
                              </div>
                              <div className="text-xs text-gray-500">
                                {new Date(
                                  emergency.timestamp || emergency.createdAt
                                ).toLocaleString()}
                              </div>
                            </div>
                            <div className="flex flex-col items-end">
                              <div
                                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(
                                  emergency.priority
                                )}`}
                              >
                                {emergency.priority} Priority
                              </div>
                              <FiChevronRight className="mt-2 text-gray-400" />
                            </div>
                          </div>
                        </button>
                      ))
                  ) : (
                    <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg flex flex-col items-center justify-center">
                      <FiInfo className="h-8 w-8 mb-2 text-gray-400" />
                      <p>No active emergencies at this time</p>
                    </div>
                  )}
                </div>

                {/* Resolved Emergencies Section */}
                <div>
                  <h2
                    className="text-base font-bold mb-3"
                    style={{ color: "#363062" }}
                  >
                    Resolved Emergencies
                    {resolvedEmergencies.length > 0 && (
                      <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {resolvedEmergencies.length}
                      </span>
                    )}
                  </h2>

                  {resolvedEmergencies.length > 0 ? (
                    resolvedEmergencies
                      .filter((emergency) =>
                        searchTerm
                          ? emergency.unitNumber
                              ?.toLowerCase()
                              .includes(searchTerm.toLowerCase()) ||
                            emergency.type
                              ?.toLowerCase()
                              .includes(searchTerm.toLowerCase()) ||
                            emergency.details
                              ?.toLowerCase()
                              .includes(searchTerm.toLowerCase())
                          : true
                      )
                      .slice(0, 5)
                      .map((emergency) => (
                        <EmergencyCard
                          key={emergency.id}
                          emergency={emergency}
                          onClick={setSelectedEmergency}
                        />
                      ))
                  ) : (
                    <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg flex flex-col items-center justify-center">
                      <FiInfo className="h-8 w-8 mb-2 text-gray-400" />
                      <p>No resolved emergencies found</p>
                    </div>
                  )}

                  {resolvedEmergencies.length > 5 && (
                    <button
                      className="w-full py-2 mt-2 text-sm hover:underline"
                      style={{ color: "#8FCD00" }}
                    >
                      View {resolvedEmergencies.length - 5} more resolved
                      emergencies
                    </button>
                  )}
                </div>
              </>
            )}

            {activeTab === "incidents" && (
              <>
                {/* Stats Cards */}
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                    <div className="flex items-center gap-2">
                      <div
                        className="p-1.5 rounded-lg"
                        style={{ backgroundColor: "#F5F5F5" }}
                      >
                        <FiClipboard style={{ color: "#363062" }} />
                      </div>
                      <div>
                        <div className="text-xs text-gray-600">Total</div>
                        <div
                          className="text-base font-semibold"
                          style={{ color: "#363062" }}
                        >
                          {incidentStats.totalIncidents}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 rounded-lg bg-green-50">
                        <FiCheckCircle className="text-green-600" />
                      </div>
                      <div>
                        <div className="text-xs text-gray-600">Resolved</div>
                        <div className="text-base font-semibold text-green-600">
                          {incidentStats.resolvedIncidents}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 rounded-lg bg-orange-50">
                        <FiClock className="text-orange-500" />
                      </div>
                      <div>
                        <div className="text-xs text-gray-600">Pending</div>
                        <div className="text-base font-semibold text-orange-500">
                          {incidentStats.pendingIncidents}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 rounded-lg bg-red-50">
                        <FiAlertTriangle className="text-red-500" />
                      </div>
                      <div>
                        <div className="text-xs text-gray-600">Recent (7d)</div>
                        <div className="text-base font-semibold text-red-500">
                          {incidentStats.recentIncidents}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Incident Reports List */}
                <div>
                  <h2
                    className="text-base font-bold mb-3"
                    style={{ color: "#363062" }}
                  >
                    Incident Reports
                    {filteredIncidents.length > 0 && (
                      <span
                        className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                        style={{ backgroundColor: "#F0F4FF", color: "#363062" }}
                      >
                        {filteredIncidents.length}
                      </span>
                    )}
                  </h2>

                  {filteredIncidents.length > 0 ? (
                    filteredIncidents.map((incident) => (
                      <IncidentCard
                        key={incident.id}
                        incident={incident}
                        onClick={setSelectedIncident}
                      />
                    ))
                  ) : (
                    <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg flex flex-col items-center justify-center">
                      <FiInfo className="h-8 w-8 mb-2 text-gray-400" />
                      <p>No incident reports found</p>
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </UserScreenWindow>
  );
}
